import { tokens } from "lib/colors"
import { LanguageContext } from "context/LanguageContext"
import Icon from "lib/components/Icons"
import Text from "lib/components/Text"
import dynamic from "next/dynamic"
import { createRef, useContext, useState } from "react"
import { optimizeImageCloudinary } from "lib/helpers"
import { isDesktop } from "react-device-detect"
import Slider from "react-slick"
import { benefitItems, Benefits } from "../types/benefit"

import "slick-carousel/slick/slick-theme.css"
import "slick-carousel/slick/slick.css"

const ImageComponent = dynamic(() => import("lib/components/Image"), { ssr: false })

const data = [
  {
    id: 1,
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675971/corporate_solution/experience-tms-1.webp",
  },
  {
    id: 2,
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675970/corporate_solution/experience-tms-2.webp",
  },
  {
    id: 3,
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675974/corporate_solution/experience-tms-3.webp",
  },
  {
    id: 4,
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675969/corporate_solution/experience-tms-4.webp",
  },
  {
    id: 5,
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675971/corporate_solution/experience-tms-5.webp",
  },
  {
    id: 6,
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675978/corporate_solution/experience-tms-6.webp",
  },
  {
    id: 7,
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675977/corporate_solution/experience-tms-7.webp",
  },
  {
    id: 8,
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675972/corporate_solution/experience-tms-8.webp",
  },
  {
    id: 9,
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675968/corporate_solution/experience-tms-9.webp",
  },
]

function TMS() {
  const { useTranslation: t } = useContext(LanguageContext)
  const benefits = t("benefits_tms") as unknown as Benefits
  const [currentSlideIndex, setCurrentSlide] = useState<number>(0)
  const customeSlider: {
    current: any
  } = createRef()

  const gotoNext = () => {
    customeSlider.current.slickNext()
  }

  const gotoPrev = () => {
    customeSlider.current.slickPrev()
  }

  const settings = {
    beforeChange: (_prev: number, next: number) => {
      setCurrentSlide(next)
    },
    dots: false,
    dotsClass: "slick-dots slick-thumb",
    arrows: false,
    infinite: true,
    slidesToShow: isDesktop ? 3 : 1,
    slidesToScroll: isDesktop ? 3 : 1,
    autoplay: false,
    cssEase: "linear",
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  }

  return (
    <div className="w-full bg-white px-5 pt-5 pb-3 rounded-2xl drop-shadow-[0px_4px_12px_rgba(23,15,73,0.08)]">
      <div className="flex flex-col gap-2 lg:gap-3">
        <div className="slider-container p-3 bg-neutral-20 rounded-xl flex flex-col gap-2">
          <div className="-mx-1.5">
            <Slider ref={customeSlider} {...settings}>
              {data.map((item) => (
                <div key={item.id} className="px-1.5">
                  <div className="relative aspect-[16/9] w-full">
                    <ImageComponent
                      src={optimizeImageCloudinary(item.image, "high", "w_650")}
                      alt={`${item.id}`}
                      objectFit="cover"
                      className="rounded-xl object-cover"
                    />
                  </div>
                </div>
              ))}
            </Slider>
          </div>
          <div className="flex flex-row items-center justify-between">
            <div className="px-4 flex gap-2 place-content-center">
              {Array.from({
                length: isDesktop ? Math.ceil(data.length / 3) : data.length,
              }).map((_, indexDot) => {
                let bgGray = "bg-neutral-40"
                const activeIndex = isDesktop
                  ? Math.floor(currentSlideIndex / 3)
                  : currentSlideIndex

                if (indexDot === activeIndex) {
                  bgGray = "bg-primary"
                }

                return (
                  <div
                    tabIndex={0}
                    aria-label="Slider"
                    role="button"
                    onClick={() =>
                      customeSlider.current.slickGoTo(isDesktop ? indexDot * 3 : indexDot)
                    }
                    key={indexDot}
                    className={`${
                      indexDot === activeIndex ? "w-4" : "w-2"
                    } h-2 ${bgGray} rounded-full cursor-pointer`}
                  />
                )
              })}
            </div>
            <div className="flex flex-row">
              <Icon
                iconName="arrowLeft"
                styles="bg-white hover:bg-primary/15 h-8 w-8 p-1 rounded-full hover:cursor-pointer hover:text-primary"
                onClick={() => gotoPrev()}
                color={tokens.neutral[50]}
              />
              <Icon
                iconName="arrow-right"
                color={tokens.neutral[50]}
                styles="bg-white hover:bg-primary/15 h-8 w-8 p-1 rounded-full hover:cursor-pointer hover:text-primary"
                onClick={() => gotoNext()}
              />
            </div>
          </div>
        </div>

        <hr className="border-t-1 border-dashed border-neutral-40 my-1" />
        <div className="flex flex-col gap-1.5">
          <Text weight="semibold" customClass="text-sm lg:text-lg">
            {benefits.title}
          </Text>
          {benefitItems.map((key) => (
            <div key={key} className="flex flex-row items-start lg:items-center gap-1">
              <div className="w-4 lg:w-[28px]">
                <Icon
                  iconName="check-badge-solid"
                  color={tokens.info.DEFAULT}
                  styles="w-4 h-5 lg:w-[28px] lg:h-[28px]"
                />
              </div>
              <Text customClass="text-sm lg:text-base" weight="medium">
                {benefits[key].title}:{" "}
                <span className="font-normal">{benefits[key].description}</span>
              </Text>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default TMS
